import {
    AlertTriangle,
    CalendarDays,
    ChevronLeft,
    ChevronRight,
    LoaderCircle,
    Plus,
    RefreshCw,
    Sparkles,
    Trash2,
    X,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './HolidayMasterScreen.module.css'

const FILTERS = ['ALL', 'NATIONAL', 'REGIONAL', 'FESTIVAL', 'SALON_SPECIFIC']

const FILTER_LABELS = {
    ALL: 'All',
    NATIONAL: 'National',
    REGIONAL: 'Regional',
    FESTIVAL: 'Festival',
    SALON_SPECIFIC: 'My Holidays',
}

const FILTER_EMPTY_LABELS = {
    NATIONAL: 'national',
    REGIONAL: 'regional',
    FESTIVAL: 'festival',
    SALON_SPECIFIC: 'my holidays',
}

const TYPE_COLOR_CLASS = {
    NATIONAL: styles.typeNational,
    REGIONAL: styles.typeRegional,
    FESTIVAL: styles.typeFestival,
    SALON_SPECIFIC: styles.typeSalon,
}

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const formatDateBadge = (holidayDate, dayOfWeek) => {
    if (!holidayDate) return '—'
    const dateObj = new Date(holidayDate)
    const day = dateObj.getDate()
    const month = dateObj.toLocaleString('en-IN', { month: 'short' })
    return `${day} ${month} · ${dayOfWeek || dateObj.toLocaleString('en-IN', { weekday: 'long' })}`
}

const HolidayMasterScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)
    const storeIdentifier = activeStore?.storeIdentifier || ''

    const [holidayData, setHolidayData] = useState(null)
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
    const [isLoading, setIsLoading] = useState(true)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [activeFilter, setActiveFilter] = useState('ALL')
    const [isDeletingId, setIsDeletingId] = useState(null)
    const [confirmSheet, setConfirmSheet] = useState(null)

    const fetchHolidayList = useCallback(async (year, { silent = false } = {}) => {
        if (!authCtx?.token || !storeIdentifier) {
            if (!silent) setIsLoading(false)
            return
        }

        if (!silent) {
            setIsLoading(true)
        } else {
            setIsRefreshing(true)
        }

        try {
            const response = await fetch(BASE_URL_API + 'getStoreHolidayList', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier,
                    year: year || new Date().getFullYear(),
                }),
            })

            if (!response.ok) {
                toast.error('Failed to load holiday calendar.')
                return
            }

            const result = await response.json()

            if (result?.status === 'success') {
                setHolidayData(result?.response || null)
                return
            }

            if (result?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            toast.error(result?.message || 'Failed to load holidays.')
        } catch (error) {
            console.error('fetchHolidayList error:', error)
            toast.error('Network error. Please check your connection.')
        } finally {
            setIsLoading(false)
            setIsRefreshing(false)
        }
    }, [authCtx, storeIdentifier])

    useEffect(() => {
        fetchHolidayList(selectedYear)
    }, [fetchHolidayList, selectedYear])

    useEffect(() => {
        if (!storeIdentifier) return
        fetchHolidayList(selectedYear, { silent: true })
    }, [location.key, fetchHolidayList, selectedYear, storeIdentifier])

    useEffect(() => {
        const onFocus = () => {
            if (!storeIdentifier) return
            fetchHolidayList(selectedYear, { silent: true })
        }

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') onFocus()
        }

        window.addEventListener('focus', onFocus)
        document.addEventListener('visibilitychange', onVisibilityChange)

        return () => {
            window.removeEventListener('focus', onFocus)
            document.removeEventListener('visibilitychange', onVisibilityChange)
        }
    }, [fetchHolidayList, selectedYear, storeIdentifier])

    const handlePrevYear = () => {
        if (selectedYear > 2020) setSelectedYear((prev) => prev - 1)
    }

    const handleNextYear = () => {
        if (selectedYear < 2035) setSelectedYear((prev) => prev + 1)
    }

    const filteredHolidays = useMemo(() => {
        const all = Array.isArray(holidayData?.holidays) ? holidayData.holidays : []
        if (activeFilter === 'ALL') return all
        if (activeFilter === 'SALON_SPECIFIC') {
            return all.filter((item) => item?.source === 'SALON')
        }
        return all.filter((item) => item?.holidayType === activeFilter)
    }, [activeFilter, holidayData?.holidays])

    const groupedHolidays = useMemo(() => {
        const grouped = {}

        filteredHolidays.forEach((holiday) => {
            const monthNum = new Date(holiday?.holidayDate).getMonth() + 1
            if (!grouped[monthNum]) grouped[monthNum] = []
            grouped[monthNum].push(holiday)
        })

        return Object.keys(grouped)
            .sort((a, b) => Number(a) - Number(b))
            .map((month) => ({
                monthNum: Number(month),
                monthName: new Date(2000, Number(month) - 1).toLocaleString('en-IN', { month: 'long' }),
                holidays: grouped[month].sort((a, b) => new Date(a?.holidayDate) - new Date(b?.holidayDate)),
            }))
    }, [filteredHolidays])

    const handleDeletePress = (holiday) => {
        setConfirmSheet({
            holidayIdentifier: holiday.holidayIdentifier,
            holidayName: holiday.holidayName,
            holidayDate: holiday.holidayDate,
        })
    }

    const deleteSalonHoliday = async (holidayIdentifier, holidayName) => {
        if (!authCtx?.token || !storeIdentifier) return

        setIsDeletingId(holidayIdentifier)
        setConfirmSheet(null)

        try {
            const response = await fetch(BASE_URL_API + 'deleteSalonHoliday', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier,
                    holidayIdentifier,
                }),
            })

            if (!response.ok) {
                toast.error('Something went wrong. Please try again.')
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                if (respo.response?.warnings?.length > 0) {
                    respo.response.warnings.forEach((w) => toast.warning(w.message, { autoClose: 4500 }))
                }
                toast.success(`"${holidayName}" removed from your calendar.`)
                fetchHolidayList(selectedYear)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'VAL_007':
                    toast.error('Holiday not found. It may have already been removed.')
                    fetchHolidayList(selectedYear)
                    break
                case 'VAL_008':
                    toast.error(respo.message)
                    break
                case 'PAY_003':
                    toast.error(`Cannot delete — "${holidayName}" is in a finalized payroll period.`)
                    break
                case 'AUTH_004':
                    toast.error('Access denied.')
                    break
                default:
                    toast.error(respo?.message || 'Failed to delete holiday.')
            }
        } catch (error) {
            console.error('deleteSalonHoliday error:', error)
            toast.error('Network error. Please check your connection.')
        } finally {
            setIsDeletingId(null)
        }
    }

    const isEmpty = !isLoading && filteredHolidays.length === 0

    const emptySubtitle = activeFilter === 'ALL'
        ? `Platform holidays for ${selectedYear} have not been loaded yet.`
        : `No ${FILTER_EMPTY_LABELS[activeFilter] || activeFilter.toLowerCase()} holidays found for ${selectedYear}.`

    const openCreateHoliday = () => {
        navigate('/payroll/setup/holiday-master/create', {
            state: {
                source: '/payroll/setup/holiday-master',
                selectedYear,
            },
        })
    }

    return (
        <div className={styles.page}>
            <section className={styles.hero}>
                <div className={styles.heroTopRow}>
                    <span className={styles.heroPill}><Sparkles size={14} /> Holiday Master</span>
                    {isRefreshing && <span className={styles.refreshHint}><RefreshCw size={13} className={styles.spin} /> Refreshing…</span>}
                </div>

                <div className={styles.heroMain}>
                    <div>
                        <h1>Holiday Calendar</h1>
                        <p>Review platform holidays, add salon-specific closure days, and keep payroll working-day calculations accurate.</p>
                    </div>

                    <div className={styles.yearPicker}>
                        <button type="button" onClick={handlePrevYear} disabled={selectedYear <= 2020}><ChevronLeft size={16} /></button>
                        <span>{selectedYear}</span>
                        <button type="button" onClick={handleNextYear} disabled={selectedYear >= 2035}><ChevronRight size={16} /></button>
                    </div>
                </div>
            </section>

            {!storeIdentifier && (
                <section className={styles.alertCard}>
                    <AlertTriangle size={16} />
                    <div>
                        <strong>Select an active store to continue.</strong>
                        <p>Holiday calendar is store-specific.</p>
                    </div>
                </section>
            )}

            <section className={styles.summaryGrid}>
                <article className={styles.summaryCard}><h4>Total</h4><strong>{Number(holidayData?.totalHolidays || 0)}</strong><span>holidays</span></article>
                <article className={styles.summaryCard}><h4>Mandatory</h4><strong>{Number(holidayData?.mandatoryHolidayCount || 0)}</strong><span>closed days</span></article>
                <article className={styles.summaryCard}><h4>Optional</h4><strong>{Number(holidayData?.optionalHolidayCount || 0)}</strong><span>informational</span></article>
                <article className={styles.summaryCard}><h4>Est. Working Days</h4><strong>{Number(holidayData?.estimatedWorkingDays || 0)}</strong><span>in {selectedYear}</span></article>
            </section>

            <section className={styles.filterRow}>
                {FILTERS.map((filterKey) => (
                    <button
                        key={filterKey}
                        type="button"
                        className={`${styles.filterTab} ${activeFilter === filterKey ? styles.filterTabActive : ''}`}
                        onClick={() => setActiveFilter(filterKey)}
                    >
                        {FILTER_LABELS[filterKey]}
                    </button>
                ))}
            </section>

            {isLoading ? (
                <section className={styles.loaderCard}>
                    <LoaderCircle size={20} className={styles.spin} />
                    <p>Loading holiday calendar...</p>
                </section>
            ) : isEmpty ? (
                <section className={styles.emptyState}>
                    <h3>No holidays found</h3>
                    <p>{emptySubtitle}</p>
                    {activeFilter !== 'ALL' && (
                        <button type="button" className={styles.clearFilterBtn} onClick={() => setActiveFilter('ALL')}>Clear filter</button>
                    )}
                </section>
            ) : (
                <section className={styles.listWrap}>
                    {groupedHolidays.map((monthGroup) => (
                        <div key={monthGroup.monthNum} className={styles.monthSection}>
                            <div className={styles.monthHeader}>
                                <CalendarDays size={15} />
                                <h3>{monthGroup.monthName} {selectedYear}</h3>
                                <span>({monthGroup.holidays.length} holiday{monthGroup.holidays.length > 1 ? 's' : ''})</span>
                            </div>

                            <div className={styles.monthCards}>
                                {monthGroup.holidays.map((holiday) => (
                                    <article key={holiday?.holidayIdentifier} className={styles.holidayCard}>
                                        <div className={styles.cardTopRow}>
                                            <span className={styles.dateBadge}>{formatDateBadge(holiday?.holidayDate, holiday?.dayOfWeek)}</span>
                                            {holiday?.canDelete && (
                                                <button
                                                    type="button"
                                                    className={styles.deleteBtn}
                                                    onClick={() => handleDeletePress(holiday)}
                                                    disabled={isDeletingId === holiday?.holidayIdentifier}
                                                >
                                                    {isDeletingId === holiday?.holidayIdentifier
                                                        ? <LoaderCircle size={14} className={styles.spin} />
                                                        : <Trash2 size={14} />}
                                                </button>
                                            )}
                                        </div>

                                        <h4>{holiday?.holidayName}</h4>

                                        <div className={styles.metaRow}>
                                            <span className={`${styles.typeChip} ${TYPE_COLOR_CLASS[holiday?.holidayType] || styles.typeNational}`}>{holiday?.holidayType}</span>
                                            {Number(holiday?.isOptional || 0) === 1 && <span className={styles.optionalPill}>Optional</span>}
                                            {Number(holiday?.isOptional || 0) === 0 && holiday?.source === 'SALON' && <span className={styles.mandatoryPill}>Mandatory</span>}
                                        </div>

                                        <p className={styles.sourceText}>{holiday?.source === 'SALON' ? 'Added by you' : 'Platform'}</p>
                                    </article>
                                ))}
                            </div>
                        </div>
                    ))}
                </section>
            )}

            <button type="button" className={styles.fab} onClick={openCreateHoliday}>
                <Plus size={16} /> Add Holiday
            </button>

            {confirmSheet && (
                <div className={styles.sheetBackdrop} onClick={() => setConfirmSheet(null)}>
                    <section className={styles.bottomSheet} onClick={(e) => e.stopPropagation()}>
                        <button type="button" className={styles.sheetClose} onClick={() => setConfirmSheet(null)}>
                            <X size={16} />
                        </button>

                        <h3>Remove Holiday?</h3>
                        <p>
                            <strong>&ldquo;{confirmSheet.holidayName}&rdquo;</strong> will be removed from your holiday calendar.
                        </p>
                        <p className={styles.sheetDate}>
                            {formatDateBadge(confirmSheet.holidayDate)}
                        </p>
                        <p className={styles.sheetNote}>
                            This cannot be undone. Staff attendance records for this date are not affected.
                        </p>

                        <div className={styles.sheetActions}>
                            <button type="button" className={styles.sheetCancel} onClick={() => setConfirmSheet(null)}>Cancel</button>
                            <button
                                type="button"
                                className={styles.sheetConfirm}
                                onClick={() => deleteSalonHoliday(confirmSheet.holidayIdentifier, confirmSheet.holidayName)}
                            >
                                <Trash2 size={14} /> Remove
                            </button>
                        </div>
                    </section>
                </div>
            )}
        </div>
    )
}

export default HolidayMasterScreen